import { useEffect, useRef } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import TextField from '@material-ui/core/TextField';
import type { ApplicationNameFieldUiOptions } from './schema';

type Props = FieldExtensionComponentProps<
  string | undefined,
  ApplicationNameFieldUiOptions
>;

/** Editable text field that prefills from an annotation on a selected entity. */
export const ApplicationNameField = (props: Props) => {
  const {
    onChange,
    schema: { title = 'Application Name', description },
    required,
    uiSchema,
    rawErrors,
    formData,
    formContext,
  } = props;
  const catalogApi = useApi(catalogApiRef);
  const options = uiSchema?.['ui:options'];
  const entityRef = options?.entityField
    ? (formContext?.formData?.[options.entityField] as string | undefined)
    : undefined;
  const annotation = options?.annotation;
  const previousEntityRef = useRef<string | undefined>(undefined);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (previousEntityRef.current === entityRef) return undefined;
    previousEntityRef.current = entityRef;
    if (!entityRef || !annotation) return undefined;

    let cancelled = false;
    catalogApi
      .getEntityByRef(entityRef)
      .then(entity => {
        const value = entity?.metadata.annotations?.[annotation];
        if (!cancelled && value) onChangeRef.current(value);
      })
      .catch(() => {
        // Keep the field editable if the catalog lookup is unavailable.
      });

    return () => {
      cancelled = true;
    };
  }, [annotation, catalogApi, entityRef]);

  return (
    <TextField
      fullWidth
      label={title}
      value={formData ?? ''}
      onChange={event => onChangeRef.current(event.target.value || undefined)}
      margin="dense"
      variant="outlined"
      required={required}
      error={rawErrors?.length > 0}
      helperText={description}
    />
  );
};
